import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { Badge } from 'primereact/badge';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import Sidebar from '../../components/empsidebar/empsidebar';
import Navbar from '../../components/empnavbar/empnavbar';
import { API_URLS } from '../../Services/servicioURL';

// Interfaces
interface TecnicoInfo {
    id: number;
    nombre: string;
    usuario: string;
}

interface PeticionType {
    tecnico: TecnicoInfo | null | undefined;
    folio: number;
    cveemp: string;
    fecha_hora: string;
    titulo: string;
    justificacion: string;
    sucursal: string;
    departamento: string;
    estatus: string;
    fechapromesa?: string | null;
    motivo?: string | null;
    confirmado?: boolean;
    fecha_confirmacion?: string | null;
    tecnico_id?: number | null;
    tecnicoAsignado?: TecnicoInfo | null;
    empleado?: {
        cveemp: string;
        nomemp: string;
    };
}

interface EmpleadoLoggeado {
    cveemp: string;
    nomemp: string;
}

interface FilterState {
    searchTerm: string;
    estatus: string;
}

// NUEVAS INTERFACES para el modal de salida
interface SalidaDetalle {
    cve_producto: string;
    cantidad: number;
    id_sucursal_destino: number;
    cve_departamento_destino: string;
    nombre_usuario: string;
}

interface SalidaCompleta {
    folio: string;
    fecha: string;
    observaciones?: string;
    salidaDetalle: SalidaDetalle[];
}

interface Producto {
    cve: string;
    descripcion: string;
    modelo: string;
}

interface Sucursal {
    id: number;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

// Constants
const ESTATUS_OPTIONS = [
    { label: 'PENDIENTE', value: 'PENDIENTE' },
    { label: 'TRABAJANDO', value: 'TRABAJANDO' },
    { label: 'LISTO', value: 'LISTO' },
    { label: 'CANCELADO', value: 'CANCELADO' }
];

const ESTATUS_COLORS: Record<string, string> = {
    PENDIENTE: '#ffc107',
    TRABAJANDO: '#28a745',
    CANCELADO: '#dc3545',
    LISTO: '#17a2b8',
    DEFAULT: '#6c757d'
};

// CORREGIDO: Utility functions para fechas
const formatDate = (fecha: string): string => {
    if (!fecha) return 'Sin fecha';
    try {
        // CORREGIDO: Crear fecha sin conversión de zona horaria
        let dateToFormat = fecha;
        
        // Si la fecha no tiene información de hora, agregarla para evitar problemas de zona horaria
        if (!fecha.includes('T') && !fecha.includes(' ')) {
            dateToFormat = fecha + 'T12:00:00';
        }
        
        const date = new Date(dateToFormat);
        
        // Verificar si la fecha es válida
        if (isNaN(date.getTime())) {
            console.warn('Fecha inválida:', fecha);
            return fecha;
        }
        
        // CORREGIDO: Usar toLocaleDateString con zona horaria específica
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: 'America/Mexico_City'
        });
    } catch (error) {
        console.error('Error formatting date:', error);
        return fecha;
    }
};

const formatDateTime = (fecha: string): string => {
    if (!fecha) return '';
    try {
        // CORREGIDO: Manejar diferentes formatos de fecha
        let dateToFormat = fecha;
        
        // Si la fecha no tiene información de zona horaria, asumimos que es local
        if (!fecha.includes('T') && !fecha.includes('Z') && !fecha.includes('+')) {
            // Si solo es fecha (YYYY-MM-DD), agregar hora del mediodía
            if (fecha.match(/^\d{4}-\d{2}-\d{2}$/)) {
                dateToFormat = fecha + 'T12:00:00';
            } else {
                // Si tiene fecha y hora pero sin zona horaria
                dateToFormat = fecha.replace(' ', 'T');
            }
        }
        
        const date = new Date(dateToFormat);
        
        // Verificar si la fecha es válida
        if (isNaN(date.getTime())) {
            console.warn('Fecha/hora inválida:', fecha);
            return fecha;
        }
        
        // CORREGIDO: Usar toLocaleString con zona horaria específica
        return date.toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Mexico_City'
        });
    } catch (error) {
        console.error('Error formatting datetime:', error);
        return fecha;
    }
};

// NUEVA FUNCIÓN: Formatear fecha solo para fechas sin hora (como fechapromesa)
const formatDateOnly = (fecha: string): string => {
    if (!fecha) return 'Sin fecha';
    try {
        // Para fechas que vienen solo como YYYY-MM-DD
        if (fecha.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = fecha.split('-');
            return `${day}/${month}/${year}`;
        }
        
        // Para otros formatos, usar la función normal
        return formatDate(fecha);
    } catch (error) {
        console.error('Error formatting date only:', error);
        return fecha;
    }
};

const getEstatusColor = (estatus: string): string => {
    if (!estatus) return ESTATUS_COLORS.DEFAULT;
    return ESTATUS_COLORS[estatus.toUpperCase()] || ESTATUS_COLORS.DEFAULT;
};

const fetchWithErrorHandling = async (url: string, options?: RequestInit) => {
    try {
        const response = await fetch(url, options);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error(`Error fetching ${url}:`, error);
        throw error;
    }
};

// NUEVAS FUNCIONES para obtener datos del modal
const obtenerProductos = async (): Promise<Producto[]> => {
    try {
        const response = await fetch(API_URLS.productos.obtener);
        if (!response.ok) throw new Error('Error al obtener los productos');
        return await response.json();
    } catch (error) {
        console.error(error);
        return [];
    }
};

const obtenerSucursales = async (): Promise<Sucursal[]> => {
    try {
        const response = await fetch(API_URLS.sucursales.obtener);
        if (!response.ok) throw new Error('Error al obtener las sucursales');
        return await response.json();
    } catch (error) {
        console.error(error);
        return [];
    }
};

const obtenerDepartamentos = async (): Promise<Departamento[]> => {
    try {
        const response = await fetch(API_URLS.departamentos.obtener);
        if (!response.ok) throw new Error('Error al obtener los departamentos');
        return await response.json();
    } catch (error) {
        console.error(error);
        return [];
    }
};

const obtenerSalidaPorFolioPeticion = async (folioPeticion: string): Promise<SalidaCompleta | null> => {
    try {
        console.log('Buscando salida para petición folio:', folioPeticion);
        
        // Primero obtener todas las salidas
        const response = await fetch(API_URLS.salidas.obtener);
        if (!response.ok) throw new Error('Error al obtener las salidas');
        
        const salidas = await response.json();
        console.log('Salidas obtenidas:', salidas);
        
        // Buscar la salida que tenga el folio de la petición en su campo folio_peticion
                const salidaEncontrada = salidas.find((salida: any) => {
                    // Buscar principalmente por folio_peticion que coincida con folio de petición
                    return salida.folio_peticion === folioPeticion || 
                        salida.folio_peticion === parseInt(folioPeticion) ||
                        salida.referencia === folioPeticion;
                });
        
        if (!salidaEncontrada) {
            console.log('No se encontró salida para la petición:', folioPeticion);
            return null;
        }
        
        console.log('Salida encontrada:', salidaEncontrada);
        return salidaEncontrada;
        
    } catch (error) {
        console.error('Error al obtener salida:', error);
        return null;
    }
};

// Custom hooks
const useFilterState = () => {
    const [filterState, setFilterState] = useState<FilterState>({
        searchTerm: '',
        estatus: ''
    });

    const updateFilter = useCallback((updates: Partial<FilterState>) => {
        setFilterState(prev => ({ ...prev, ...updates }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilterState({
            searchTerm: '',
            estatus: ''
        });
    }, []);

    return { filterState, updateFilter, resetFilters };
};

// Main component
const EmpInicio: React.FC = () => {
    // State
    const [peticiones, setPeticiones] = useState<PeticionType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [empleadoLoggeado, setEmpleadoLoggeado] = useState<EmpleadoLoggeado | null>(null);
    const [confirmandoPeticion, setConfirmandoPeticion] = useState<number | null>(null);
    const [motivoDialogVisible, setMotivoDialogVisible] = useState<boolean>(false);
    const [motivoSeleccionado, setMotivoSeleccionado] = useState<string>('');
    
    // NUEVOS ESTADOS para el modal de salida
    const [isModalSalidaVisible, setIsModalSalidaVisible] = useState<boolean>(false);
    const [selectedSalida, setSelectedSalida] = useState<SalidaCompleta | null>(null);
    const [loadingSalida, setLoadingSalida] = useState<boolean>(false);
    const [productos, setProductos] = useState<Producto[]>([]);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    
    const { filterState, updateFilter, resetFilters } = useFilterState();
    
    const toast = useRef<Toast>(null);

    // Estados para el tour guiado
    const [runTour, setRunTour] = useState<boolean>(false);
    const [tourCompleted, setTourCompleted] = useState<boolean>(false);

    // Definición de los pasos del tour
    const tourSteps: Step[] = [
    {
        target: 'body',
        content: (
            <div>
                <h4>👋 Bienvenido al Dashboard de Empleados</h4>
                <p>Haz clic en "Siguiente" para conocer cómo gestionar tus peticiones.</p>
            </div>
        ),
        placement: 'center',
        disableBeacon: true,
    },
    {
        target: '[data-tour="stats"]',
        content: (
            <div>
                <h4>📊 Estadísticas</h4>
                <p>Aquí puedes ver un resumen de todas tus peticiones por estado.</p>
            </div>
        ),
        placement: 'bottom',
    },
    {
        target: '[data-tour="search"]',
        content: (
            <div>
                <h4>🔍 Búsqueda y Filtros</h4>
                <p>Usa estos filtros para encontrar peticiones específicas por folio, título, técnico o estatus.</p>
            </div>
        ),
        placement: 'bottom',
    },
    {
        target: '[data-tour="table"]',
        content: (
            <div>
                <h4>📋 Tabla de Peticiones</h4>
                <p>Aquí se muestran todas tus peticiones. Las columnas incluyen:</p>
                <ul style={{ textAlign: 'left', margin: '10px 0', paddingLeft: '20px' }}>
                    <li><strong>📄 Folio:</strong> Número único de identificación</li>
                    <li><strong>🔄 Estatus:</strong> Estado actual de la petición</li>
                    <li><strong>👨‍💻 Técnico:</strong> Técnico asignado a tu petición</li>
                </ul>
                <p><strong>💡 Tip:</strong> Puedes hacer clic en las peticiones que están "LISTO" o "CONFIRMADO" para ver los detalles de salida.</p>
            </div>
        ),
        placement: 'bottom',
    },
    {
        target: '[data-tour="actions-info"]',
        content: (
            <div>
                <h4>⚡ Acciones Disponibles</h4>
                <p>Dependiendo del estado de tu petición, podrás:</p>
                <ul style={{ textAlign: 'left', margin: '10px 0', paddingLeft: '20px' }}>
                    <li><strong>✅ Confirmar:</strong> Si está "LISTO", puedes confirmar que la recibiste</li>
                    <li><strong>👁️ Ver Salida:</strong> Si está "LISTO" o "CONFIRMADO", puedes ver los detalles</li>
                    <li><strong>🔄 Actualizar:</strong> Usa el botón "Actualizar" para recargar los datos</li>
                </ul>
            </div>
        ),
        placement: 'left',
    },
    {
        target: 'body',
        content: (
            <div>
                <h4>🎉 ¡Tour Completado!</h4>
                <p>Ya conoces todas las funciones del dashboard. Algunas recomendaciones finales:</p>
                <ul style={{ textAlign: 'left', margin: '10px 0', paddingLeft: '20px' }}>
                    <li>Revisa regularmente el estado de tus peticiones</li>
                    <li>Confirma las peticiones cuando estén listas</li>
                    <li>Usa los filtros para encontrar peticiones específicas</li>
                    <li>Haz clic en peticiones completadas para ver detalles de salida</li>
                </ul>
                <p><strong>¡Ahora puedes gestionar tus peticiones eficientemente!</strong></p>
            </div>
        ),
        placement: 'center',
    },
];

    // Callback al finalizar el tour
    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status } = data;
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setRunTour(false);
            setTourCompleted(true);
        }
    };

    
    // Función para obtener empleado loggeado
    const obtenerEmpleadoLoggeado = useCallback((): EmpleadoLoggeado | null => {
        try {
            const authToken = localStorage.getItem('authToken2');
            if (!authToken || authToken !== 'logueado') {
                return null;
            }

            const cveemp = localStorage.getItem('cveEmpleado');
            const nomemp = localStorage.getItem('nombreEmpleado');
            
            if (!cveemp || !nomemp) {
                return null;
            }

            return { cveemp, nomemp };
        } catch (error) {
            console.error('Error al obtener empleado loggeado:', error);
            return null;
        }
    }, []);
    

    // NUEVA FUNCIÓN: Verificar si se puede hacer clic en la petición para ver salida
    const puedeVerSalida = useCallback((estatus: string, confirmado?: boolean): boolean => {
        const estatusPermitidos = ['LISTO', 'CONFIRMADO', 'COMPLETADA', 'ENTREGADO', 'COMPLETADO'];
        const estatusNormalizado = estatus.toUpperCase().trim();
        
        // Si está confirmado, también puede ver la salida
        if (confirmado === true) {
            return true;
        }
        
        return estatusPermitidos.includes(estatusNormalizado);
    }, []);

    // NUEVA FUNCIÓN: Manejar clic en fila de petición (CORREGIDA)
    
const handleRowClick = useCallback(async (peticion: any) => {
    // Cast seguro del tipo
    const peticionData = peticion as PeticionType;
    
    console.log('Petición seleccionada:', peticionData);
    console.log('Estatus de la petición:', peticionData.estatus);
    console.log('¿Puede ver salida?', puedeVerSalida(peticionData.estatus, peticionData.confirmado));
    
    // Si el estatus permite ver la salida, mostrar modal de salida
    if (puedeVerSalida(peticionData.estatus, peticionData.confirmado)) {
        console.log('Mostrando modal de salida para petición:', peticionData.folio);
        
        setLoadingSalida(true);
        setIsModalSalidaVisible(true);
        setSelectedSalida(null); // Limpiar salida anterior
        
        try {
            const salidaData = await obtenerSalidaPorFolioPeticion(peticionData.folio.toString());
            console.log('Datos de salida obtenidos:', salidaData);
            
            if (salidaData) {
                setSelectedSalida(salidaData);
            }
            // No cerrar el modal, mostrar mensaje en el modal si no hay datos
        } catch (error) {
            console.error('Error al obtener salida:', error);
            // No cerrar el modal, mostrar mensaje de error en el modal
        } finally {
            setLoadingSalida(false);
        }
    }
    // No mostrar mensaje para peticiones que no están listas
}, [puedeVerSalida]);

    // NUEVAS FUNCIONES auxiliares para el modal de salida
    const getDescripcionProducto = useCallback((cve_producto: string): string => {
        const producto = productos.find(p => p.cve === cve_producto);
        return producto ? producto.descripcion : 'Producto no encontrado';
    }, [productos]);

    const getNombreSucursal = useCallback((id: number): string => {
        const suc = sucursales.find(s => s.id === id);
        return suc ? suc.nombre : `ID: ${id}`;
    }, [sucursales]);

    const getNombreDepartamento = useCallback((cve: string): string => {
        const dept = departamentos.find(d => d.cve === cve);
        return dept ? dept.nombre : cve;
    }, [departamentos]);

  // Memoized values
const filteredPeticiones = useMemo(() => {
    if (!Array.isArray(peticiones)) return [];
    
    let filtered = [...peticiones];

    if (filterState.searchTerm) {
        const searchLower = filterState.searchTerm.toLowerCase().trim();
        filtered = filtered.filter(peticion => {
            // Campos básicos de la petición
            const folio = peticion.folio?.toString().toLowerCase() || '';
            const titulo = peticion.titulo?.toLowerCase() || '';
            const estatus = peticion.estatus?.toLowerCase() || '';
            const motivo = peticion.motivo?.toLowerCase() || '';
            const justificacion = peticion.justificacion?.toLowerCase() || '';
            
            // Técnico asignado (si existe)
            const tecnicoAsignadoNombre = peticion.tecnicoAsignado?.nombre?.toLowerCase() || '';
            const tecnicoAsignadoUsuario = peticion.tecnicoAsignado?.usuario?.toLowerCase() || '';
            
            // Técnico (si existe)
            const tecnicoNombre = peticion.tecnico?.nombre?.toLowerCase() || '';
            const tecnicoUsuario = peticion.tecnico?.usuario?.toLowerCase() || '';
            
            // Empleado solicitante
            const empleadoNombre = peticion.empleado?.nomemp?.toLowerCase() || '';
            const empleadoCve = peticion.empleado?.cveemp?.toLowerCase() || '';
            const cveemp = peticion.cveemp?.toLowerCase() || '';
            
            // NUEVO: Buscar en todos los campos de texto disponibles
            // Esto incluirá cualquier mención del técnico en título, justificación, etc.
            const todosLosCampos = [
                folio,
                titulo,
                estatus,
                motivo,
                justificacion,
                tecnicoAsignadoNombre,
                tecnicoAsignadoUsuario,
                tecnicoNombre,
                tecnicoUsuario,
                empleadoNombre,
                empleadoCve,
                cveemp
            ].join(' ').toLowerCase();
            
            return todosLosCampos.includes(searchLower);
        });
    }

    if (filterState.estatus) {
        filtered = filtered.filter(peticion => peticion.estatus === filterState.estatus);
    }

    return filtered;
}, [peticiones, filterState]);

    // Estadísticas
    const estadisticas = useMemo(() => {
        const total = peticiones.length;
        const pendientes = peticiones.filter(p => p.estatus === 'PENDIENTE').length;
        const trabajando = peticiones.filter(p => p.estatus === 'TRABAJANDO').length;
        const listas = peticiones.filter(p => p.estatus === 'LISTO').length;
        const canceladas = peticiones.filter(p => p.estatus === 'CANCELADO').length;
        const confirmadas = peticiones.filter(p => p.confirmado === true).length;
        const pendientesConfirmacion = peticiones.filter(p => p.estatus === 'LISTO' && p.confirmado === false).length;
        const conTecnico = peticiones.filter(p => p.tecnicoAsignado !== null && p.tecnicoAsignado !== undefined).length;
        const sinTecnico = peticiones.filter(p => !p.tecnicoAsignado).length;

        return { total, pendientes, trabajando, listas, canceladas, confirmadas, pendientesConfirmacion, conTecnico, sinTecnico };
    }, [peticiones]);

    // API functions
    const cargarPeticiones = useCallback(async () => {
    if (!empleadoLoggeado) return;

    setLoading(true);
    try {
        console.log('Cargando peticiones para empleado:', empleadoLoggeado.cveemp);
        
        const peticionesData = await fetchWithErrorHandling(
            API_URLS.peticiones.obtenerPorEmpleado(empleadoLoggeado.cveemp)
        );
        
        console.log('Peticiones obtenidas:', peticionesData);
        
        // NUEVO: Enriquecer peticiones con datos completos del técnico
        const peticionesEnriquecidas = await Promise.all(
            (Array.isArray(peticionesData) ? peticionesData : []).map(async (peticion) => {
                try {
                    // Si tiene tecnico_id pero no tiene datos completos del técnico, cargarlos
                    if (peticion.tecnico_id && (!peticion.tecnico || !peticion.tecnico.nombre)) {
                        console.log(`Cargando datos del técnico ID: ${peticion.tecnico_id}`);
                        
                        const response = await fetch(API_URLS.usuarios.obtenerPorId(peticion.tecnico_id.toString()));
                        if (response.ok) {
                            const tecnicoData = await response.json();
                            peticion.tecnico = {
                                id: tecnicoData.id,
                                nombre: tecnicoData.nombre,
                                usuario: tecnicoData.usuario
                            };
                            console.log(`Técnico cargado: ${tecnicoData.nombre}`);
                        } else {
                            console.warn(`No se pudo cargar técnico ID: ${peticion.tecnico_id}`);
                        }
                    }
                    
                    // Si tiene tecnicoAsignado con ID pero sin datos completos, cargarlos
                    if (peticion.tecnicoAsignado?.id && !peticion.tecnicoAsignado.nombre) {
                        console.log(`Cargando datos del técnico asignado ID: ${peticion.tecnicoAsignado.id}`);
                        
                        const response = await fetch(API_URLS.usuarios.obtenerPorId(peticion.tecnicoAsignado.id.toString()));
                        if (response.ok) {
                            const tecnicoData = await response.json();
                            peticion.tecnicoAsignado = {
                                id: tecnicoData.id,
                                nombre: tecnicoData.nombre,
                                usuario: tecnicoData.usuario
                            };
                            console.log(`Técnico asignado cargado: ${tecnicoData.nombre}`);
                        } else {
                            console.warn(`No se pudo cargar técnico asignado ID: ${peticion.tecnicoAsignado.id}`);
                        }
                    }
                } catch (error) {
                    console.error(`Error cargando datos del técnico para petición ${peticion.folio}:`, error);
                }
                
                return peticion;
            })
        );
        
        console.log('Peticiones enriquecidas con técnicos:', peticionesEnriquecidas);
        setPeticiones(peticionesEnriquecidas);
        
    } catch (error) {
        console.error('Error loading peticiones:', error);
        toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar las peticiones',
            life: 3000
        });
    } finally {
        setLoading(false);
    }
}, [empleadoLoggeado]);

    // NUEVA FUNCIÓN: Cargar datos para el modal
    const cargarDatosModal = useCallback(async () => {
        try {
            const [productosData, sucursalesData, departamentosData] = await Promise.all([
                obtenerProductos(),
                obtenerSucursales(),
                obtenerDepartamentos()
            ]);
            
            setProductos(productosData);
            setSucursales(sucursalesData);
            setDepartamentos(departamentosData);
        } catch (error) {
            console.error('Error al cargar datos para modal:', error);
        }
    }, []);

    // NUEVA FUNCIÓN: Confirmar petición
    const confirmarPeticion = useCallback(async (folio: number) => {
        setConfirmandoPeticion(folio);
        
        try {
            const response = await fetch(`${API_URLS.peticiones.actualizar(folio.toString()).replace('/actualizarPeticion/', '/confirmarPeticion/')}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al confirmar petición');
            }

            const result = await response.json();
            
            toast.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Petición confirmada exitosamente',
                life: 3000
            });

            // Recargar peticiones para mostrar el cambio
            await cargarPeticiones();
            
        } catch (error: any) {
            console.error('Error al confirmar petición:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Error al confirmar la petición',
                life: 4000
            });
        } finally {
            setConfirmandoPeticion(null);
        }
    }, [cargarPeticiones]);

    // NUEVA FUNCIÓN: Mostrar diálogo de confirmación
    const mostrarConfirmacionConfirmar = useCallback((peticion: PeticionType) => {
        confirmDialog({
            message: `¿Está seguro de que desea confirmar la petición ${peticion.folio}?`,
            header: 'Confirmar Petición',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Sí, Confirmar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-success',
            accept: () => confirmarPeticion(peticion.folio),
            reject: () => {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Cancelado',
                    detail: 'Confirmación cancelada',
                    life: 2000
                });
            }
        });
    }, [confirmarPeticion]);

    // NUEVA FUNCIÓN: Mostrar motivo completo
    const mostrarMotivoCompleto = useCallback((motivo: string) => {
        setMotivoSeleccionado(motivo);
        setMotivoDialogVisible(true);
    }, []);

    // Column templates
    const estatusBodyTemplate = useCallback((rowData: PeticionType) => {
        const isConfirmado = rowData.confirmado === true;
        const backgroundColor = isConfirmado ? '#28a745' : getEstatusColor(rowData.estatus);
        const displayText = isConfirmado ? 'CONFIRMADO' : (rowData.estatus || 'N/A');
        const puedeVer = puedeVerSalida(rowData.estatus, rowData.confirmado);
        
        return (
            <div style={{ textAlign: 'center' }}>
                <Badge 
                    value={displayText}
                    style={{ 
                        backgroundColor: backgroundColor,
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        cursor: puedeVer ? 'pointer' : 'default',
                        opacity: puedeVer ? 1 : 0.7
                    }}
                    title={puedeVer ? 'Clic para ver detalles de salida' : 'Sin salida disponible'}
                />
                {puedeVer && <i className="pi pi-external-link" style={{ marginLeft: '5px', fontSize: '10px', color: '#007bff' }}></i>}
            </div>
        );
    }, [puedeVerSalida]);

    // NUEVO: Template para mostrar información del técnico (MEJORADO)
const tecnicoBodyTemplate = useCallback((rowData: PeticionType) => {
    // Debug: Log para ver qué datos llegan
    console.log('Datos del técnico para petición', rowData.folio, ':', {
        tecnicoAsignado: rowData.tecnicoAsignado,
        tecnico: rowData.tecnico,
        tecnico_id: rowData.tecnico_id,
        completeRowData: rowData
    });

    // Verificar múltiples formas de acceder al técnico
    const tecnicoAsignado = rowData.tecnicoAsignado;
    const tecnico = rowData.tecnico;
    const tecnicoId = rowData.tecnico_id;

    // Prioridad 1: tecnicoAsignado con información completa
    if (tecnicoAsignado && tecnicoAsignado.nombre) {
        const { nombre, usuario } = tecnicoAsignado;
        const displayName = nombre.length > 15 ? nombre.substring(0, 15) + '...' : nombre;
        
        return (
            <div style={{ 
                fontSize: '0.85rem',
                lineHeight: '1.2'
            }}>
                <div style={{ 
                    fontWeight: 'bold',
                    color: '#007bff',
                    marginBottom: '2px'
                }}
                title={nombre}
                >
                    <i className="pi pi-user" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                    {displayName}
                </div>
            </div>
        );
    }

    // Prioridad 2: tecnico con información completa
    if (tecnico && tecnico.nombre) {
        const { nombre, usuario } = tecnico;
        const displayName = nombre.length > 15 ? nombre.substring(0, 15) + '...' : nombre;
        
        return (
            <div style={{ 
                fontSize: '0.85rem',
                lineHeight: '1.2'
            }}>
                <div style={{ 
                    fontWeight: 'bold',
                    color: '#007bff',
                    marginBottom: '2px'
                }}
                title={nombre}
                >
                    <i className="pi pi-user" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                    {displayName}
                </div>
            </div>
        );
    }

    // Prioridad 3: Solo hay ID de técnico, mostrar componente que busque la información
    if (tecnicoId) {
        return <TecnicoInfoComponent tecnicoId={tecnicoId} />;
    }

    // Sin técnico asignado
    return (
        <span style={{ 
            color: '#6c757d', 
            fontStyle: 'italic',
            fontSize: '0.8rem'
        }}>
            <i className="pi pi-user-minus" style={{ fontSize: '10px', marginRight: '4px' }}></i>
            Sin asignar
        </span>
    );
}, []);

// NUEVO: Componente para obtener y mostrar información del técnico por ID
const TecnicoInfoComponent: React.FC<{ tecnicoId: number }> = ({ tecnicoId }) => {
    const [tecnicoInfo, setTecnicoInfo] = useState<TecnicoInfo | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const obtenerTecnicoInfo = async () => {
            try {
                setLoading(true);
                const response = await fetch(API_URLS.usuarios.obtenerPorId(tecnicoId.toString()));
                if (response.ok) {
                    const userData = await response.json();
                    setTecnicoInfo({
                        id: userData.id,
                        nombre: userData.nombre,
                        usuario: userData.usuario
                    });
                } else {
                    console.warn(`No se pudo obtener información del técnico ID: ${tecnicoId}`);
                    setTecnicoInfo(null);
                }
            } catch (error) {
                console.error(`Error al obtener técnico ID ${tecnicoId}:`, error);
                setTecnicoInfo(null);
            } finally {
                setLoading(false);
            }
        };

        obtenerTecnicoInfo();
    }, [tecnicoId]);

    if (loading) {
        return (
            <div style={{ 
                fontSize: '0.8rem',
                color: '#6c757d'
            }}>
                <i className="pi pi-spinner pi-spin" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                Cargando...
            </div>
        );
    }

    if (!tecnicoInfo) {
        return (
            <div style={{ 
                fontSize: '0.8rem',
                color: '#dc3545'
            }}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                ID: {tecnicoId} (No encontrado)
            </div>
        );
    }

    const { nombre, usuario } = tecnicoInfo;
    const displayName = nombre.length > 15 ? nombre.substring(0, 15) + '...' : nombre;

    return (
        <div style={{ 
            fontSize: '0.85rem',
            lineHeight: '1.2'
        }}>
            <div style={{ 
                fontWeight: 'bold',
                color: '#007bff',
                marginBottom: '2px'
            }}
            title={nombre}
            >
                <i className="pi pi-user" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                {displayName}
            </div>
        </div>
    );
};

    // NUEVA FUNCIÓN: Template para botón de confirmación
    const accionesBodyTemplate = useCallback((rowData: PeticionType) => {
        const puedeConfirmar = rowData.estatus === 'LISTO' && rowData.confirmado !== true;
        const estaConfirmando = confirmandoPeticion === rowData.folio;
        
        if (!puedeConfirmar) {
            return null;
        }

        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    icon="pi pi-check"
                    label="Confirmar"
                    className="p-button-success p-button-sm"
                    onClick={(e) => {
                        e.stopPropagation(); // Evitar que se dispare el click de la fila
                        mostrarConfirmacionConfirmar(rowData);
                    }}
                    loading={estaConfirmando}
                    disabled={estaConfirmando}
                    tooltip="Confirmar que la petición está lista"
                    tooltipOptions={{ position: 'top' }}
                    style={{ fontSize: '11px', padding: '4px 8px' }}
                />
            </div>
        );
    }, [confirmandoPeticion, mostrarConfirmacionConfirmar]);

    // Effects
    useEffect(() => {
        const empleado = obtenerEmpleadoLoggeado();
        if (empleado) {
            setEmpleadoLoggeado(empleado);
        } else {
            toast.current?.show({
                severity: 'warn',
                summary: 'Sesión no válida',
                detail: 'No se encontró información del empleado loggeado',
                life: 3000
            });
        }
    }, [obtenerEmpleadoLoggeado]);

    useEffect(() => {
        if (empleadoLoggeado) {
            cargarPeticiones();
            cargarDatosModal(); // Cargar datos para el modal
        }
    }, [empleadoLoggeado, cargarPeticiones, cargarDatosModal]);

    // Verificar si el tour ya fue completado
    useEffect(() => {
        const tourCompletedStorage = localStorage.getItem('tourCompleted_empinicio');
        if (tourCompletedStorage === 'true') {
            setTourCompleted(true);
        }
    }, []);

    // Render loading state
    if (!empleadoLoggeado) {
        return (
            <>
                
                <Toast ref={toast} />
                
                <div className="content-wrapper" style={{
                    backgroundImage: 'url(/dist/img/FondoLogin.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '16px',
                        padding: '40px',
                        maxWidth: '400px',
                        textAlign: 'center',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#f39c12', marginBottom: '20px' }}></i>
                        <h3 style={{ color: '#1f2937', marginBottom: '16px' }}>Sesión no válida</h3>
                        <p style={{ color: '#6b7280', marginBottom: '24px' }}>No se pudo identificar al empleado loggeado. Por favor, inicie sesión nuevamente.</p>
                        <Button 
                            label="Ir al Login" 
                            icon="pi pi-sign-in" 
                            className="p-button-primary"
                            onClick={() => window.location.href = '/login-empleados'}
                            style={{ 
                                padding: '12px 24px',
                                fontSize: '1rem',
                                fontWeight: '600',
                                borderRadius: '8px'
                            }}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            
            <Sidebar />
                <Navbar />
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Tour Guiado */}
            <Joyride
                steps={tourSteps}
                run={runTour}
                continuous={true}
                showProgress={true}
                showSkipButton={true}
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        zIndex: 10000,
                        primaryColor: '#3b82f6',
                        textColor: '#1f2937',
                        backgroundColor: '#ffffff',
                        arrowColor: '#ffffff',
                    },
                    tooltip: {
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    },
                }}
            />
            
            {/* Diálogo para mostrar motivo completo */}
            <Dialog
                header="📝 Motivo Completo"
                visible={motivoDialogVisible}
                style={{  width: '50vw', minWidth: '300px' }}
                onHide={() => setMotivoDialogVisible(false)}
                modal
                
            >
                <div style={{ padding: '10px 0' }}>
                    <p style={{ 
                        lineHeight: '1.6', 
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                    }}>
                        {motivoSeleccionado || 'Sin motivo especificado'}
                    </p>
                </div>
            </Dialog>

            {/* NUEVO MODAL - Detalles de Salida */}
           <Dialog
    header={`📦 Detalles de Salida - Petición ${selectedSalida?.folio || ''}`}
    visible={isModalSalidaVisible}
    style={{ width: '95%', maxWidth: '1400px' }}
    onHide={() => {
        setIsModalSalidaVisible(false);
        setSelectedSalida(null);
        setLoadingSalida(false);
    }}
    
    modal 
>
    {loadingSalida ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
            <i className="pi pi-spinner pi-spin" style={{ fontSize: '3rem', color: '#007bff', marginBottom: '20px' }}></i>
            <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>Cargando detalles de la salida...</p>
        </div>
    ) : selectedSalida ? (
        <>
            {/* Encabezado de Salida - Más Compacto */}
            <div style={{ marginBottom: '20px' }}>
                <h4 style={{ 
                    color: '#007bff', 
                    borderBottom: '2px solid #007bff', 
                    paddingBottom: '8px',
                    margin: '0 0 15px 0',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    📋 Encabezado de Salida
                </h4>
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '16px',
                    backgroundColor: 'rgba(248, 249, 250, 0.8)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #dee2e6'
                }}>
                    <div>
                        <strong style={{ color: '#495057', fontSize: '0.9rem', display: 'block', marginBottom: '4px' }}>📄 Folio:</strong>
                        <div style={{ fontWeight: 'bold', color: '#007bff', fontSize: '1.1rem' }}>
                            {selectedSalida.folio}
                        </div>
                    </div>
                    <div>
                        <strong style={{ color: '#495057', fontSize: '0.9rem', display: 'block', marginBottom: '4px' }}>📅 Fecha:</strong>
                        <div style={{ fontSize: '1rem' }}>
                            {formatDate(selectedSalida.fecha)}
                        </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <strong style={{ color: '#495057', fontSize: '0.9rem', display: 'block', marginBottom: '4px' }}>📝 Observaciones:</strong>
                        <div style={{ fontSize: '1rem', fontStyle: selectedSalida.observaciones ? 'normal' : 'italic', color: selectedSalida.observaciones ? 'inherit' : '#6c757d' }}>
                            {selectedSalida.observaciones || 'Sin observaciones'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Detalle de Productos - Tabla Compacta */}
            <div>
                <h4 style={{
                    color: '#28a745',
                    borderBottom: '2px solid #28a745',
                    paddingBottom: '8px',
                    margin: '0 0 15px 0',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    📦 Detalle de Productos
                </h4>
                
                <div style={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #e5e7eb'
                }}>
                    <DataTable
                        value={selectedSalida.salidaDetalle || []}
                        scrollable
                        scrollHeight="400px"
                        stripedRows
                        size="small"
                        emptyMessage="❌ No hay detalles de productos disponibles"
                        rowHover
                        style={{
                            fontSize: '0.85rem'
                        }}
                    >
                        <Column
                            field="cve_producto"
                            header="🔑 Clave"
                            style={{
                                width: '100px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: '#007bff'
                            }}
                        />
                        <Column
                            header="📦 Descripción"
                            body={(rowData) => (
                                <div 
                                    style={{ 
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        lineHeight: '1.3',
                                        padding: '4px 0'
                                    }}
                                    title={getDescripcionProducto(rowData.cve_producto)}
                                >
                                    {getDescripcionProducto(rowData.cve_producto)}
                                </div>
                            )}
                            style={{
                                minWidth: '200px'
                            }}
                        />
                        <Column
                            field="cantidad"
                            header="📊 Cant."
                            body={(rowData) => (
                                <div style={{ textAlign: 'center' }}>
                                    <Badge 
                                        value={rowData.cantidad} 
                                        severity="info"
                                        style={{ fontSize: '0.8rem' }}
                                    />
                                </div>
                            )}
                            style={{
                                width: '80px',
                                textAlign: 'center'
                            }}
                        />
                        <Column
                            header="🏢 Sucursal"
                            body={(rowData) => (
                                <div 
                                    style={{ textAlign: 'center' }}
                                    title={getNombreSucursal(rowData.id_sucursal_destino)}
                                >
                                    {getNombreSucursal(rowData.id_sucursal_destino)}
                                </div>
                            )}
                            style={{
                                width: '120px'
                            }}
                        />
                        <Column
                            header="🏬 Depto."
                            body={(rowData) => (
                                <div 
                                    style={{ textAlign: 'center' }}
                                    title={getNombreDepartamento(rowData.cve_departamento_destino)}
                                >
                                    {getNombreDepartamento(rowData.cve_departamento_destino)}
                                </div>
                            )}
                            style={{
                                width: '120px'
                            }}
                        />
                        <Column
                            header="👤 Usuario"
                            body={(rowData) => (
                                <div 
                                    style={{ 
                                        textAlign: 'center',
                                        color: '#28a745',
                                        fontWeight: '500'
                                    }}
                                    title={rowData.nombre_usuario || 'Sin asignar'}
                                >
                                    {rowData.nombre_usuario || 'Sin asignar'}
                                </div>
                            )}
                            style={{
                                width: '100px'
                            }}
                        />
                    </DataTable>
                </div>

                {/* Resumen de productos */}
                {selectedSalida.salidaDetalle && selectedSalida.salidaDetalle.length > 0 && (
                    <div style={{ 
                        marginTop: '15px',
                        padding: '12px 20px',
                        backgroundColor: 'rgba(227, 242, 253, 0.8)',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        color: '#1565c0',
                        textAlign: 'center',
                        border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                        <strong>
                            📊 Total de productos: {selectedSalida.salidaDetalle.length} | 
                            Cantidad total: {selectedSalida.salidaDetalle.reduce((sum, item) => sum + item.cantidad, 0)}
                        </strong>
                    </div>
                )}
            </div>

            {/* Footer del modal */}
            <div style={{ 
                marginTop: '20px', 
                textAlign: 'center',
                padding: '16px',
                backgroundColor: 'rgba(212, 237, 218, 0.8)',
                borderRadius: '8px',
                border: '1px solid #c3e6cb'
            }}>
                <p style={{ 
                    margin: 0, 
                    color: '#155724', 
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                }}>
                    ✅ Salida procesada correctamente
                </p>
            </div>
        </>
    ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '20px' }}></i>
            <h4 style={{ marginTop: '15px', color: '#856404', fontSize: '1.3rem', marginBottom: '16px' }}>
                No se encontró información de salida
            </h4>
            <p style={{ color: '#6c757d', fontSize: '1rem', lineHeight: '1.5' }}>
                No se encontraron detalles de salida asociados a esta petición.
                <br />
                Es posible que la salida aún no haya sido procesada o que no haya salida por petición.
            </p>
        </div>
    )}
</Dialog>
            
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                
                {/* Botón para iniciar el tour */}
                {!tourCompleted && (
                    <div style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        zIndex: 1001
                    }}>
                        <Button
                            label="🎯 Iniciar Tour"
                            icon="pi pi-info-circle"
                            severity="help"
                            raised
                            onClick={() => setRunTour(true)}
                            style={{
                                borderRadius: '8px',
                                fontWeight: '600',
                                fontSize: '1rem'
                            }}
                        />
                    </div>
                )}
                
                {/* Header principal mejorado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div>
                            <h1 style={{ 
                                margin: '0 0 8px 0', 
                                color: '#1f2937', 
                                fontSize: '2rem', 
                                fontWeight: '700',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                🏠 Dashboard - Mis Peticiones
                            </h1>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                color: '#6b7280',
                                fontSize: '1rem'
                            }}>
                                <span>👤 Bienvenido, <strong style={{ color: '#1565c0' }}>{empleadoLoggeado.nomemp}</strong></span>
                                <span>•</span>
                                <span>Código: <strong style={{ color: '#1976d2' }}>{empleadoLoggeado.cveemp}</strong></span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                backgroundColor: loading ? '#ffc107' : '#10b981',
                                color: 'white',
                                borderRadius: '20px',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                            }}>
                                <i className={loading ? 'pi pi-spinner pi-spin' : 'pi pi-check-circle'}></i>
                                {loading ? 'CARGANDO...' : 'DATOS LISTOS'}
                            </div>
                            <Button 
                                    icon="pi pi-refresh" 
                                    label="Actualizar" 
                                    severity="info"
                                    onClick={cargarPeticiones}
                                    loading={loading}
                                    data-tour="actions-info"  
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: '600'
                                    }}
                                />
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Estadísticas mejoradas */}
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                    gap: '16px', 
                    marginBottom: '20px' 
                }} data-tour="stats">
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '12px',
                        padding: '20px',
                        textAlign: 'center',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                        <i className="pi pi-list" style={{ fontSize: '2.5rem', color: '#6c757d', marginBottom: '12px' }}></i>
                        <h3 style={{ margin: '0 0 4px 0', color: '#6c757d', fontSize: '2rem', fontWeight: 'bold' }}>{estadisticas.total}</h3>
                        <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Total</p>
                    </div>
                    
                    <div style={{
                        backgroundColor: 'rgba(255, 243, 205, 0.95)',
                        borderRadius: '12px',
                        padding: '20px',
                        textAlign: 'center',
                        boxShadow: '04px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
            <i className="pi pi-clock" style={{ fontSize: '2.5rem', color: '#856404', marginBottom: '12px' }}></i>
            <h3 style={{ margin: '0 0 4px 0', color: '#856404', fontSize: '2rem', fontWeight: 'bold' }}>{estadisticas.pendientes}</h3>
            <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Pendientes</p>
        </div>

        <div style={{
            backgroundColor: 'rgba(212, 237, 218, 0.95)',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(40, 167, 69, 0.3)',
            transition: 'transform 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
            <i className="pi pi-cog" style={{ fontSize: '2.5rem', color: '#155724', marginBottom: '12px' }}></i>
            <h3 style={{ margin: '0 0 4px 0', color: '#155724', fontSize: '2rem', fontWeight: 'bold' }}>{estadisticas.trabajando}</h3>
            <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Trabajando</p>
        </div>

        <div style={{
            backgroundColor: 'rgba(209, 236, 241, 0.95)',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(23, 162, 184, 0.3)',
            transition: 'transform 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
            <i className="pi pi-check" style={{ fontSize: '2.5rem', color: '#0c5460', marginBottom: '12px' }}></i>
            <h3 style={{ margin: '0 0 4px 0', color: '#0c5460', fontSize: '2rem', fontWeight: 'bold' }}>{estadisticas.listas}</h3>
            <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Listas</p>
        </div>

        <div style={{
            backgroundColor: 'rgba(248, 215, 218, 0.95)',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(220, 53, 69, 0.3)',
            transition: 'transform 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
            <i className="pi pi-times-circle" style={{ fontSize: '2.5rem', color: '#721c24', marginBottom: '12px' }}></i>
            <h3 style={{ margin: '0 0 4px 0', color: '#721c24', fontSize: '2rem', fontWeight: 'bold' }}>{estadisticas.canceladas}</h3>
                    <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Canceladas</p>
                </div>

                <div style={{
                    backgroundColor: 'rgba(212, 237, 218, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '2px solid #28a745',
                    transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <i className="pi pi-verified" style={{ fontSize: '2.5rem', color: '#155724', marginBottom: '12px' }}></i>
                    <h3 style={{ margin: '0 0 4px 0', color: '#155724', fontSize: '2rem', fontWeight: 'bold' }}>{estadisticas.confirmadas}</h3>
                    <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Confirmadas</p>
                </div>

                <div style={{
                    backgroundColor: 'rgba(255, 243, 205, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '2px solid #ffc107',
                    transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '2.5rem', color: '#856404', marginBottom: '12px' }}></i>
                    <h3 style={{ margin: '0 0 4px 0', color: '#856404', fontSize: '2rem', fontWeight: 'bold' }}>{estadisticas.pendientesConfirmacion}</h3>
                    <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Por Confirmar</p>
                </div>
            </div>

            {/* Tabla Principal de Peticiones */}
            <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '16px',
                padding: '0',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                overflow: 'hidden'
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <i className="pi pi-table" style={{ fontSize: '1.5rem' }}></i>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>📋 Historial de Peticiones</h2>
                </div>

                {/* Filtros mejorados */}
                <div style={{
                    padding: '20px 24px',
                    backgroundColor: 'rgba(248, 249, 250, 0.8)',
                    borderBottom: '1px solid #e5e7eb'
                }} data-tour="search">
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto',
                        gap: '16px',
                        alignItems: 'center'
                    }}>
                        <div style={{ position: 'relative' }}>
                            <i className="pi pi-search" style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#6b7280',
                                fontSize: '1rem'
                            }}></i>
                            <InputText
                                placeholder="🔍 Buscar por folio, título, técnico, estatus, motivo..."
                                value={filterState.searchTerm}
                                onChange={(e) => updateFilter({ searchTerm: e.target.value })}
                                style={{
                                    width: '100%',
                                    paddingLeft: '40px',
                                    borderRadius: '8px',
                                    border: '2px solid #e5e7eb',
                                    fontSize: '1rem',
                                    padding: '12px 12px 12px 40px'
                                }}
                            />
                        </div>

                        <Dropdown
                            value={filterState.estatus}
                            options={ESTATUS_OPTIONS}
                            onChange={(e) => updateFilter({ estatus: e.value })}
                            placeholder="Filtrar por estatus"
                            style={{
                                minWidth: '200px',
                                borderRadius: '8px'
                            }}
                        />

                        <Button
                            icon="pi pi-filter-slash"
                            label="Limpiar Filtros"
                            severity="secondary"
                            onClick={resetFilters}
                            style={{
                                borderRadius: '8px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap'
                            }}
                        />
                    </div>

                    {/* Indicador de resultados */}
                    <div style={{
                        marginTop: '12px',
                        padding: '8px 12px',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        color: '#1e40af',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <i className="pi pi-info-circle"></i>
                        <span>
                            Mostrando <strong>{filteredPeticiones.length}</strong> de <strong>{peticiones.length}</strong> peticiones
                            {filterState.searchTerm && ` • Búsqueda: "${filterState.searchTerm}"`}
                            {filterState.estatus && ` • Estatus: ${filterState.estatus}`}
                        </span>
                    </div>
                </div>

                {/* DataTable completamente responsiva */}
<div style={{ padding: '0' }} data-tour="table">
    <DataTable
        value={filteredPeticiones}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        loading={loading}
        emptyMessage={
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <i className="pi pi-inbox" style={{ fontSize: '3rem', marginBottom: '16px', display: 'block' }}></i>
                <h3 style={{ margin: '0 0 8px 0' }}>No hay peticiones</h3>
                <p style={{ margin: 0 }}>
                    {filterState.searchTerm || filterState.estatus 
                        ? 'No se encontraron peticiones con los filtros aplicados'
                        : 'No tienes peticiones registradas'
                    }
                </p>
            </div>
        }
        className="p-datatable-sm responsive-table-complete"
        style={{ fontSize: '0.9rem' }}
        rowHover
        stripedRows
        responsiveLayout="scroll"
        onRowClick={(e) => handleRowClick(e.data)}
        rowClassName={(rowData) => {
            const puedeVer = puedeVerSalida(rowData.estatus, rowData.confirmado);
            return puedeVer ? 'cursor-pointer row-clickable' : '';
        }}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} peticiones"
        scrollable
        scrollHeight="70vh"
    >
        <Column
            field="folio"
            header="📄 Folio"
            style={{ 
                minWidth: '70px',
                width: '70px',
                textAlign: 'center', 
                fontWeight: 'bold'
            }}
            sortable
            body={(rowData) => (
                <Badge
                    value={rowData.folio}
                    severity="info"
                    className="folio-badge"
                />
            )}
            className="col-folio"
            data-tour="folio"
        />

        <Column
            header="📅 Fecha"
            body={(rowData) => (
                <div className="fecha-container">
                    <div className="fecha-principal">
                        {formatDate(rowData.fecha_hora)}
                    </div>
                    <div className="fecha-hora">
                        {new Date(rowData.fecha_hora).toLocaleTimeString('es-ES', {
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </div>
                </div>
            )}
            style={{ minWidth: '100px', width: '100px' }}
            sortable
            sortField="fecha_hora"
            className="col-fecha"
        />

        <Column
            header="📝 Título"
            body={(rowData) => {
                const titulo = rowData.titulo || '';
                const puedeVer = puedeVerSalida(rowData.estatus, rowData.confirmado);
                
                return (
                    <div 
                        title={titulo}
                        className={`titulo-container ${puedeVer ? 'titulo-clickable' : ''}`}
                    >
                        {titulo}
                    </div>
                );
            }}
            style={{ minWidth: '150px' }}
            sortable
            sortField="titulo"
            className="col-titulo"
        />

        <Column
            header="🔄 Estatus"
            body={estatusBodyTemplate}
            style={{ minWidth: '100px', width: '120px', textAlign: 'center' }}
            sortable
            sortField="estatus"
            className="col-estatus"
            data-tour="estatus"
        />

        <Column
            header="👨‍💻 Técnico"
            body={tecnicoBodyTemplate}
            style={{ minWidth: '120px', width: '150px' }}
            sortable
            sortField="tecnicoAsignado.nombre"
            className="col-tecnico"
            data-tour="tecnico"
        />

        <Column
            header="⏰ Promesa"
            body={(rowData) => (
                <div className="promesa-container">
                    {rowData.fechapromesa ? formatDateOnly(rowData.fechapromesa) : 
                        <span className="sin-fecha">Sin fecha</span>
                    }
                </div>
            )}
            style={{ minWidth: '100px', width: '120px' }}
            sortable
            sortField="fechapromesa"
            className="col-promesa"
        />

        <Column
            header="💬 Motivo"
            body={(rowData) => {
                const motivo = rowData.motivo || 'Sin motivo';
                const maxLength = 25;
                const isTruncated = motivo.length > maxLength;
                
                return (
                    <div className="motivo-container">
                        <div className="motivo-texto" title={motivo}>
                            {isTruncated 
                                ? motivo.substring(0, maxLength) + '...' 
                                : motivo}
                        </div>
                        {isTruncated && (
                            <Button
                                icon="pi pi-eye"
                                className="p-button-text p-button-sm motivo-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    mostrarMotivoCompleto(motivo);
                                }}
                                tooltip="Ver completo"
                                tooltipOptions={{ position: 'top' }}
                            />
                        )}
                    </div>
                );
            }}
            style={{ minWidth: '100px', width: '130px' }}
            sortable
            sortField="motivo"
            className="col-motivo"
        />

        <Column
            header="✅ Confirmación"
            body={(rowData) => {
                if (!rowData.confirmado || !rowData.fecha_confirmacion) {
                    return <span className="no-confirmada">No confirmada</span>;
                }
                
                return (
                    <div className="confirmacion-container">
                        <div className="confirmacion-fecha">{formatDate(rowData.fecha_confirmacion)}</div>
                        <div className="confirmacion-hora">
                            {new Date(rowData.fecha_confirmacion).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </div>
                    </div>
                );
            }}
            style={{ minWidth: '100px', width: '120px' }}
            sortable
            sortField="fecha_confirmacion"
            className="col-confirmacion"
        />

        <Column
            header="⚡ Acciones"
            body={accionesBodyTemplate}
            style={{ minWidth: '90px', width: '90px', textAlign: 'center' }}
            frozen
            alignFrozen="right"
            className="col-acciones"
        />
    </DataTable>
</div>
            </div>
        </div>

        {/* Estilos CSS adicionales mejorados */}
        <style>
            {`
                /* CSS para DataTable Completamente Responsiva */

/* Estilos base para la tabla */
.cursor-pointer {
    cursor: pointer !important;
}

.row-clickable:hover {
    background-color: rgba(59, 130, 246, 0.05) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.p-datatable .p-datatable-tbody > tr.cursor-pointer {
    transition: all 0.2s ease;
}

.p-datatable .p-datatable-thead > tr > th {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    color: #1e293b;
    font-weight: 700;
    border-bottom: 2px solid #3b82f6;
    padding: 16px 12px;
    font-size: 0.9rem;
}

.p-datatable .p-datatable-tbody > tr > td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

.p-datatable .p-datatable-tbody > tr:nth-child(even) {
    background-color: rgba(248, 250, 252, 0.5);
}

/* Estilos específicos para cada columna */

/* Columna Folio */
.folio-badge {
    font-size: 0.8rem !important;
    padding: 4px 8px !important;
}

/* Columna Fecha */
.fecha-container {
    text-align: center;
    font-size: 0.8rem;
}

.fecha-principal {
    font-weight: bold;
    margin-bottom: 2px;
    line-height: 1.2;
}

.fecha-hora {
    color: #6b7280;
    font-size: 0.7rem;
}

/* Columna Título */
.titulo-container {
    word-wrap: break-word;
    whiteSpace: normal;
    line-height: 1.3;
    padding: 4px 0;
    font-size: 0.85rem;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.titulo-clickable {
    cursor: pointer;
    color: #007bff;
    text-decoration: underline;
}

/* Columna Técnico */
.col-tecnico {
    font-size: 0.85rem;
}

.col-tecnico .pi-user {
    font-size: 10px;
    margin-right: 4px;
}

/* Columna Promesa */
.promesa-container {
    font-size: 0.8rem;
    text-align: center;
}

.sin-fecha {
    color: #6c757d;
    font-style: italic;
}

/* Columna Motivo */
.motivo-container {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 150px;
}

.motivo-texto {
    flex: 1;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.2;
    padding: 2px 0;
    font-size: 0.8rem;
    overflow: hidden;
    text-overflow: ellipsis;
}

.motivo-btn {
    padding: 1px 3px !important;
    font-size: 0.7rem !important;
    min-width: auto !important;
    width: 20px !important;
    height: 20px !important;
}

/* Columna Confirmación */
.confirmacion-container {
    color: #28a745;
    font-weight: bold;
    font-size: 0.75rem;
    text-align: center;
}

.confirmacion-fecha {
    line-height: 1.2;
}

.confirmacion-hora {
    font-size: 0.7rem;
    opacity: 0.8;
}

.no-confirmada {
    color: #6c757d;
    font-style: italic;
    font-size: 0.75rem;
}

/* Columna Acciones */
.col-acciones .p-button {
    font-size: 11px !important;
    padding: 4px 8px !important;
}

/* Paginador */
.p-paginator {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    border-top: 2px solid #3b82f6;
    padding: 16px;
}

.p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
    background: #3b82f6;
    color: white;
}

/* Estilos para formularios */
.p-dropdown {
    border: 2px solid #e5e7eb;
}

.p-dropdown:not(.p-disabled):hover {
    border-color: #3b82f6;
}

.p-dropdown:not(.p-disabled).p-focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.p-inputtext {
    transition: all 0.2s ease;
}

.p-inputtext:enabled:hover {
    border-color: #3b82f6;
}

.p-inputtext:enabled:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.p-button {
    transition: all 0.2s ease;
}

.p-button:enabled:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Estilos para diálogos */
.p-dialog .p-dialog-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 12px 12px 0 0;
}

.p-dialog .p-dialog-content {
    border-radius: 0 0 12px 12px;
}

.p-toast .p-toast-message {
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

/* RESPONSIVIDAD COMPLETA */

/* Tablets grandes (1200px - 1024px) */
@media (max-width: 1200px) {
    .p-datatable .p-datatable-thead > tr > th {
        padding: 12px 8px;
        font-size: 0.85rem;
    }
    
    .p-datatable .p-datatable-tbody > tr > td {
        padding: 10px 6px;
    }
    
    .titulo-container {
        max-width: 150px;
        font-size: 0.8rem;
    }
    
    .motivo-container {
        max-width: 120px;
    }
    
    .motivo-texto {
        font-size: 0.75rem;
    }
}

/* Tablets (1024px - 768px) */
@media (max-width: 1024px) {
    .p-datatable .p-datatable-thead > tr > th {
        padding: 10px 6px;
        font-size: 0.8rem;
    }
    
    .p-datatable .p-datatable-tbody > tr > td {
        padding: 8px 4px;
        font-size: 0.8rem;
    }
    
    .folio-badge {
        font-size: 0.7rem !important;
        padding: 3px 6px !important;
    }
    
    .fecha-container {
        font-size: 0.7rem;
    }
    
    .fecha-principal {
        font-size: 0.7rem;
    }
    
    .fecha-hora {
        font-size: 0.6rem;
    }
    
    .titulo-container {
        max-width: 120px;
        font-size: 0.75rem;
    }
    
    .col-tecnico {
        font-size: 0.75rem;
    }
    
    .promesa-container {
        font-size: 0.7rem;
    }
    
    .motivo-container {
        max-width: 100px;
    }
    
    .motivo-texto {
        font-size: 0.7rem;
    }
    
    .confirmacion-container {
        font-size: 0.7rem;
    }
    
    .confirmacion-hora {
        font-size: 0.6rem;
    }
    
    .col-acciones .p-button {
        font-size: 10px !important;
        padding: 3px 6px !important;
    }
}

/* Móviles grandes (768px - 480px) */
@media (max-width: 768px) {
    .p-datatable .p-datatable-thead > tr > th {
        padding: 8px 4px;
        font-size: 0.75rem;
    }
    
    .p-datatable .p-datatable-tbody > tr > td {
        padding: 6px 3px;
        font-size: 0.75rem;
    }
    
    .folio-badge {
        font-size: 0.65rem !important;
        padding: 2px 4px !important;
    }
    
    .fecha-container {
        font-size: 0.65rem;
    }
    
    .fecha-principal {
        font-size: 0.65rem;
    }
    
    .fecha-hora {
        font-size: 0.55rem;
    }
    
    .titulo-container {
        max-width: 100px;
        font-size: 0.7rem;
    }
    
    .col-tecnico {
        font-size: 0.7rem;
    }
    
    .col-tecnico .pi-user {
        font-size: 8px;
    }
    
    .promesa-container {
        font-size: 0.65rem;
    }
    
    .motivo-container {
        max-width: 80px;
    }
    
    .motivo-texto {
        font-size: 0.65rem;
    }
    
    .motivo-btn {
        width: 18px !important;
        height: 18px !important;
        font-size: 0.6rem !important;
    }
    
    .confirmacion-container {
        font-size: 0.65rem;
    }
    
    .confirmacion-hora {
        font-size: 0.55rem;
    }
    
    .no-confirmada {
        font-size: 0.65rem;
    }
    
    .col-acciones .p-button {
        font-size: 9px !important;
        padding: 2px 4px !important;
    }
    
    /* Ajustar anchos mínimos para móviles */
    .col-folio {
        min-width: 60px !important;
        width: 60px !important;
    }
    
    .col-fecha {
        min-width: 80px !important;
        width: 80px !important;
    }
    
    .col-titulo {
        min-width: 100px !important;
    }
    
    .col-estatus {
        min-width: 80px !important;
        width: 80px !important;
    }
    
    .col-tecnico {
        min-width: 90px !important;
    }
    
    .col-promesa {
        min-width: 70px !important;
        width: 70px !important;
    }
    
    .col-motivo {
        min-width: 80px !important;
    }
    
    .col-confirmacion {
        min-width: 80px !important;
        width: 80px !important;
    }
    
    .col-acciones {
        min-width: 70px !important;
        width: 70px !important;
    }
}

/* Móviles pequeños (480px - 320px) */
@media (max-width: 480px) {
    .p-datatable .p-datatable-thead > tr > th {
        padding: 6px 2px;
        font-size: 0.7rem;
    }
    
    .p-datatable .p-datatable-tbody > tr > td {
        padding: 4px 2px;
        font-size: 0.7rem;
    }
    
    .folio-badge {
        font-size: 0.6rem !important;
        padding: 1px 3px !important;
    }
    
    .fecha-container {
        font-size: 0.6rem;
    }
    
    .fecha-principal {
        font-size: 0.6rem;
    }
    
    .fecha-hora {
        font-size: 0.5rem;
    }
    
    .titulo-container {
        max-width: 80px;
        font-size: 0.65rem;
    }
    
    .col-tecnico {
        font-size: 0.65rem;
    }
    
    .promesa-container {
        font-size: 0.6rem;
    }
    
    .motivo-container {
        max-width: 70px;
    }
    
    .motivo-texto {
        font-size: 0.6rem;
    }
    
    .motivo-btn {
        width: 16px !important;
        height: 16px !important;
        font-size: 0.55rem !important;
    }
    
    .confirmacion-container {
        font-size: 0.6rem;
    }
    
    .confirmacion-hora {
        font-size: 0.5rem;
    }
    
    .col-acciones .p-button {
        font-size: 8px !important;
        padding: 1px 3px !important;
    }
    
    /* Anchos ultra compactos para móviles pequeños */
    .col-folio {
        min-width: 50px !important;
        width: 50px !important;
    }
    
    .col-fecha {
        min-width: 70px !important;
        width: 70px !important;
    }
    
    .col-titulo {
        min-width: 80px !important;
    }
    
    .col-estatus {
        min-width: 70px !important;
        width: 70px !important;
    }
    
    .col-tecnico {
        min-width: 80px !important;
    }
    
    .col-promesa {
        min-width: 60px !important;
        width: 60px !important;
    }
    
    .col-motivo {
        min-width: 70px !important;
    }
    
    .col-confirmacion {
        min-width: 70px !important;
        width: 70px !important;
    }
    
    .col-acciones {
        min-width: 60px !important;
        width: 60px !important;
    }
}

/* Scroll horizontal para pantallas muy pequeñas */
@media (max-width: 320px) {
    .responsive-table-complete {
        min-width: 800px;
    }
    
    .p-datatable-wrapper {
        overflow-x: auto;
    }
}

/* Mejoras adicionales para la experiencia móvil */
@media (max-width: 768px) {
    .p-paginator {
        padding: 8px;
        flex-wrap: wrap;
        gap: 8px;
    }
    
    .p-paginator .p-paginator-current {
        order: -1;
        width: 100%;
        text-align: center;
        margin-bottom: 8px;
        font-size: 0.8rem;
    }
    
    .p-paginator .p-dropdown {
        font-size: 0.8rem;
    }
    
    .p-paginator .p-paginator-pages .p-paginator-page {
        min-width: 32px;
        height: 32px;
        font-size: 0.8rem;
    }
}
            `}
        </style>
        </>
    );
};

export default EmpInicio;